import { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { TOP_BANNER_TYPE_CLOSED } from 'application/consts';
import { useBillingInfo } from '@entities/billing';

import { AuthCurrentUserDocument, AuthCurrentUserQuery, Country, PaymentKind } from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';

export enum BANNER_TYPE {
  GRACE = 'GRACE',
  UNPAID = 'UNPAID',
  TRIAL = 'TRIAL',
}

export type TopBannerState = {
  type: BANNER_TYPE;
  trialDaysLeft?: number;
};

export const resetTopBannerTypeClosed = () => localStorage.removeItem(TOP_BANNER_TYPE_CLOSED);

function useTopBannerState() {
  const { data } = useQuery<AuthCurrentUserQuery>(AuthCurrentUserDocument, {
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const [hiddenTopBannerState, setHiddenTopBannerState] = useState(
    localStorage.getItem(TOP_BANNER_TYPE_CLOSED) as BANNER_TYPE | undefined
  );

  const currentUser = data?.currentUserPureQuery;
  const currentCompany = currentUser?.mainCompany;

  const billingInfo = useBillingInfo(currentCompany?.id);

  const trialPeriod = currentCompany?.trialPeriod;

  const trialDaysLeft = useMemo(
    () => DateUtils.getDaysLeft(trialPeriod?.lengthMinutes, trialPeriod?.lengthDays, trialPeriod?.startedAt),
    [trialPeriod?.lengthMinutes, trialPeriod?.lengthDays, trialPeriod?.startedAt]
  );

  const isBillingEnabled = !!currentCompany?.isBillingEnabled;

  const isGraceBanner = isBillingEnabled && billingInfo.nextBillingDate && billingInfo.hasGraceLicenses;

  const isUnpaidBanner = isBillingEnabled && billingInfo.hasInactiveLicenses;

  const isTrialBannerKz =
    currentCompany?.country === Country.Kz &&
    currentCompany?.trialPeriod?.isFinished === false &&
    currentCompany?.trialPeriod?.isExpired === false;

  const isTrialBannerBy =
    currentCompany?.country === Country.By &&
    currentCompany?.trialPeriod?.isFinished === false &&
    currentCompany?.trialPeriod?.isExpired === false;

  const isTrialBanner = billingInfo.hasTrialLicenses;

  let banner: TopBannerState | undefined;

  if (currentCompany) {
    if (isTrialBanner || isTrialBannerKz || isTrialBannerBy) {
      banner = {
        type: BANNER_TYPE.TRIAL,
        trialDaysLeft,
      };
    }

    if (isGraceBanner) {
      banner = {
        type: BANNER_TYPE.GRACE,
      };
    }

    if (isUnpaidBanner) {
      banner = {
        type: BANNER_TYPE.UNPAID,
      };
    }
  }

  return {
    banner,
    isVisible: !!banner && banner?.type !== hiddenTopBannerState,
    isSBBOLPayment: billingInfo?.selectedPaymentKind === PaymentKind.Sbbol,
    onClose: () => {
      setHiddenTopBannerState(banner?.type);
      if (banner?.type) {
        localStorage.setItem(TOP_BANNER_TYPE_CLOSED, banner?.type);
      } else {
        resetTopBannerTypeClosed();
      }
    },
    country: currentCompany?.country || Country.Ru,
  };
}

export default useTopBannerState;
